/**
 * Contemplative & Thought-Provoking Quiz for Surah 32 (As-Sajdah)
 * Designed for deep reflection (Tadabbur), linguistic subtlety, and thematic coherence.
 */

export const SURAH_32_QUIZ = {
  surahNumber: 32,
  surahName: 'As-Sajdah',
  surahNameAr: 'السجدة',
  title: 'Contemplative Study: Surah As-Sajdah',
  subtitle: '10 Thought-Provoking Inquiries on Human Psychology, Linguistic Precision, and Divine Coherence',
  description: 'These questions are not meant to test rote memory of words or verses. Instead, they invite you into deep contemplation (Tadabbur)—exploring why specific words were chosen, what psychological motives drive skepticism, and how the architecture of the Surah speaks to the human heart.',
  questions: [
    {
      id: 1,
      ayahRef: 'Ayahs 7–9',
      title: 'The Sudden Shift to "You" (Iltifāt)',
      prompt: 'In verses 7–9, the Quran describes the creation of human beings starting from clay, speaking about man in the third person ("He began the creation of man... He made his progeny..."). But when mentioning hearing, sight, and emotional faculties, the language suddenly shifts to direct personal address ("and gave YOU hearing... how little gratitude YOU show!"). What is the profound literary and psychological purpose of this sudden shift?',
      options: [
        {
          id: 'A',
          text: 'It transforms the description of human creation from an objective biology lesson into intimate, personal accountability for the sensory gifts you enjoy right now.',
          isCorrect: true
        },
        {
          id: 'B',
          text: 'It is a poetic variation used solely to maintain Arabic rhyme and meter across the endings of the verses.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'It indicates that hearing and emotional perception only belonged to later generations, unlike the early humans.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'It signals that physical bodily creation requires no gratitude, whereas sensory organs do.',
          isCorrect: false
        }
      ],
      deepReflection: 'In classical Arabic rhetoric, this technique is known as Iltifāt (a deliberate shift in perspective). When discussing human biology and embryonic development, one can easily listen passively like a student in an anatomy lecture. But the moment hearing, sight, and emotional-intellectual faculties (al-af\'idah) are mentioned, Allah strips away the third-person distance and looks directly at YOU. You cannot remain an aloof spectator; the very ears with which you are listening to this recitation are an intimate divine loan for which you are personally answerable.'
    },
    {
      id: 2,
      ayahRef: 'Ayah 10',
      title: 'The Real Motive Behind Skepticism',
      prompt: 'The skeptics questioned resurrection by asking: "When we are lost [disintegrated] into the earth, will we really be in a new creation?" (Ayah 10). How does the Quran immediately expose the true psychological motive behind their objection in the very next sentence?',
      options: [
        {
          id: 'A',
          text: 'They genuinely doubted God\'s power to reconstruct physical matter and were asking for scientific proof.',
          isCorrect: false
        },
        {
          id: 'B',
          text: 'Their intellectual query was a smokescreen; their true aversion was moral accountability and facing their Lord ("Bal hum bi-liqā\'i Rabbihim kāfirūn").',
          isCorrect: true
        },
        {
          id: 'C',
          text: 'They had studied ancient Greek philosophy and rejected the concept of bodily resurrection.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'They believed that the soul dies with the body and cannot exist without flesh.',
          isCorrect: false
        }
      ],
      deepReflection: 'Notice the diagnostic precision of the Quran: The skeptics framed their dilemma as a scientific impossibility ("How can dust scattered in soil be reconstituted?"). But Allah exposes that human skepticism is rarely about a lack of evidence; it is about resisting moral consequence. Deep down, they do not want an afterlife because an afterlife demands accounting for how they lived. The intellectual question about "how" is simply a defense mechanism to avoid "Who" they must eventually meet.'
    },
    {
      id: 3,
      ayahRef: 'Ayah 16',
      title: 'The Restless Bed of the Believer',
      prompt: 'In verse 16, Allah depicts those who rise for night prayer with the striking phrase "Tatajāfā junūbuhum \'anil madāji\'" (literally: "their sides violently detach or rebel against their beds"). What vivid psychological and emotional reality does this unique word portray?',
      options: [
        {
          id: 'A',
          text: 'They suffer from physical insomnia and night anxiety caused by everyday stress.',
          isCorrect: false
        },
        {
          id: 'B',
          text: 'Even though the bed is soft, warm, and inviting, their longing for intimate conversation with their Master makes staying in bed feel spiritually uncomfortable and unfulfilling.',
          isCorrect: true
        },
        {
          id: 'C',
          text: 'Their beds are intentionally made from hard rocks to ensure they wake up in pain.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'They are forced awake by an external obligation that they find burdensome and harsh.',
          isCorrect: false
        }
      ],
      deepReflection: 'The root j-f-w (جَفَا) carries the meaning of roughness, incompatibility, and mutual repulsion. Normally, a cozy bed (madja\') is the most irresistible haven after a grueling day. Yet for these believers, the spiritual gravitational pull of their Lord is so powerful that remaining asleep feels uncomfortable. The bed seems to push their sides away, because their soul simply cannot rest until it has poured itself out in the peaceful silence of the night.'
    },
    {
      id: 4,
      ayahRef: 'Ayah 17',
      title: 'Divine Symmetry: Secret Deed, Hidden Reward',
      prompt: 'Verse 17 promises: "No soul knows what comfort of the eyes has been hidden for them" (mā ukhfiya lahum min qurrati a\'yun). Why did Allah specifically choose the passive verb "ukhfiya" ("has been hidden away / kept secret") for these night worshippers?',
      options: [
        {
          id: 'A',
          text: 'Because the angels did not have enough time to record the details of Paradise.',
          isCorrect: false
        },
        {
          id: 'B',
          text: 'Because the reward is so delicate that announcing it would spoil the surprise for all creation.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'As a profound divine reciprocity: just as they concealed their tears and devotion in the pitch darkness where no human eye could see, Allah kept their ultimate reward concealed beyond human imagination.',
          isCorrect: true
        },
        {
          id: 'D',
          text: 'Because eye pleasures are considered secondary to other physical pleasures in Jannah.',
          isCorrect: false
        }
      ],
      deepReflection: 'There is a breathtaking poetic justice (Jazā\'un wifāqā) in divine generosity. When you wake up in the dead of night, there is no public audience, no social validation, and no applause. It is an entirely private, hidden devotion. In direct correspondence, Allah prepares a reward that is likewise hidden (ukhfiya)—a bespoke comfort of the eyes so intensely personal and magnificent that no created imagination could ever foresee it.'
    },
    {
      id: 5,
      ayahRef: 'Ayah 15',
      title: 'The Falling Forehead vs. The Standing Ego',
      prompt: 'When genuine believers are reminded of divine signs, verse 15 states: "They fall down into prostration (kharrū sujjadan) and celebrate the praises of their Lord, and they are not arrogant." Why does the verse explicitly emphasize the absence of pride ("wa hum lā yastakbirūn") immediately after the physical posture of prostration?',
      options: [
        {
          id: 'A',
          text: 'Because physical prostration is easy to perform, but genuinely surrendering one\'s ego, biases, and pride to the truth is the real internal challenge.',
          isCorrect: true
        },
        {
          id: 'B',
          text: 'Because people who prostrate often forget to say words of praise while on the floor.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'Because only physically flexible people are able to fall down into prostration quickly.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'Because ancient prostration was an aristocratic ritual that was associated with social prestige.',
          isCorrect: false
        }
      ],
      deepReflection: 'The verb kharrū describes collapsing downwards involuntarily, as if gravity and sheer awe overpowered their limbs. Yet anyone can mechanically lower their head to the ground while their heart remains standing upright in stubborn conceit. Real Sajdah is not merely a geometric motion; it is the total capitulation of arrogance. The Quran reminds us that bending the neck is meaningless unless the self-important ego bows down alongside it.'
    },
    {
      id: 6,
      ayahRef: 'Ayah 18',
      title: 'The Natural Protection of Boundaries (Fisq)',
      prompt: 'Verse 18 asks: "Is the one who was a believer like the one who was a Fāsiq? They are not equal." In classical Arabic origin, what physical picture does the word "Fāsiq" (فَاسِق) paint, and what does it reveal about the nature of disobedience?',
      options: [
        {
          id: 'A',
          text: 'It describes a ripe date that bursts out of its protective skin, symbolizing a human being who ruptures the protective envelope of divine guidance.',
          isCorrect: true
        },
        {
          id: 'B',
          text: 'It describes a blind camel wandering aimlessly through the desert dunes without water.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'It refers to a broken bow that can no longer shoot arrows with accuracy.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'It denotes a merchant who was cheated of his capital during a trading journey.',
          isCorrect: false
        }
      ],
      deepReflection: 'In ancient Arabic, when a date fruit over-ripened under the desert sun and ruptured its outer skin, the Arabs said "fasaqat ar-rutabah" (the date committed fisq). That delicate skin was not a prison; it was the date\'s sole defense against rotting insects and drying winds. Similarly, divine moral limits are not tyrannical cages; they are the protective membrane shielding the human soul from corruption. A fāsiq is someone who violently breaks out of that divine safety, leaving themselves exposed to spiritual ruin.'
    },
    {
      id: 7,
      ayahRef: 'Ayah 21',
      title: 'The Therapeutic Purpose of Worldly Pain',
      prompt: 'In verse 21, Allah states: "We will surely make them taste the nearer punishment before the greater punishment, so that perhaps they may return." What does this verse teach about the deeper wisdom behind worldly distress and hardship?',
      options: [
        {
          id: 'A',
          text: 'Worldly tribulations are pure vindictive retaliation with no higher purpose or redemption.',
          isCorrect: false
        },
        {
          id: 'B',
          text: 'Worldly hardships are therapeutic alarms of divine mercy, designed to awaken us from moral slumber before we face the irreversible consequence.',
          isCorrect: true
        },
        {
          id: 'C',
          text: 'Pain in this life only strikes those who have never committed any moral mistakes.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'The greater punishment only applies to people who lived lives entirely free of difficulty.',
          isCorrect: false
        }
      ],
      deepReflection: 'When a caring physician identifies early symptoms of a deadly disease, they may prescribe an uncomfortable, bitter treatment to save the patient\'s life. Worldly afflictions—financial anxiety, illness, shattered illusions, grief—are small "tastes" (al-\'adhāb al-adnā) meant to dislodge our complacency. They are not final judgments; they are emergency sirens. The operative clause is "la\'allahum yarji\'ūn" (so that they may turn back). The pain has a redemptive mission: to steer you safely home.'
    },
    {
      id: 8,
      ayahRef: 'Ayah 24',
      title: 'The Twin Engines of Spiritual Leadership',
      prompt: 'Regarding the leaders appointed among the followers of Moses, verse 24 highlights two specific virtues: "We made from among them leaders guiding by Our command when they practiced patience (sabarū) and possessed firm conviction in Our signs (kānū bi-āyātinā yūqinūn)." Why are Sabr and Yaqīn the indispensable formula for leadership?',
      options: [
        {
          id: 'A',
          text: 'Sabr protects against emotional burnout, anger, and desire, while Yaqīn protects against intellectual doubt, skepticism, and moral confusion.',
          isCorrect: true
        },
        {
          id: 'B',
          text: 'Sabr is needed to memorize lengthy scripture, while Yaqīn is only needed when confronting foreign armies.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'Sabr applies only to financial poverty, while Yaqīn applies only to physical healing.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'They are synonyms in classical Arabic and are repeated merely for poetic emphasis.',
          isCorrect: false
        }
      ],
      deepReflection: 'The human spirit is vulnerable from two primary gateways: Shahawāt (passions, greed, and fatigue) and Shubahāt (intellectual doubts and cynicism). Without Yaqīn (unshakeable clarity and conviction in divine promise), a leader wavers and second-guesses when facing philosophical skepticism or hardship. Without Sabr (ironclad emotional restraint and perseverance), a leader sells out under pressure or gives in to bitterness. When unshakable conviction meets steadfast endurance, divine leadership (Imāmah) is born.'
    },
    {
      id: 9,
      ayahRef: 'Ayahs 26–27',
      title: 'Hearing History vs. Seeing Nature',
      prompt: 'In verse 26, after describing past destroyed nations whose ruined dwellings people walk through, Allah asks: "Will they not hear?" (afalā yasma\'ūn). But in the very next verse, when describing rain reviving parched barren land, He asks: "Will they not see?" (afalā yubsirūn). What is the deliberate sensory brilliance of this pairing?',
      options: [
        {
          id: 'A',
          text: 'Historical events are accessed through historical reports and testimonies (which must be HEARD), whereas the miracle of biological revival unfolds before your very eyes (which must be SEEN).',
          isCorrect: true
        },
        {
          id: 'B',
          text: 'Ancient peoples were all deaf, whereas desert soil has acute vision.',
          isCorrect: false
        },
        {
          id: 'C',
          text: 'Hearing is reserved for scholars, while seeing is reserved for manual laborers.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'The Quran alternates verbs at random purely to break repetitive phrasing.',
          isCorrect: false
        }
      ],
      deepReflection: 'Observe the immaculate sensory alignment: You cannot time-travel to physically gaze upon ancient civilizations that perished centuries ago; you can only hear and heed their recorded chronicles and oral histories (afalā yasma\'ūn). But you do not need to rely on oral history to witness resurrection—step outside and observe how dead, bone-dry desert soil (al-ard al-juruz) springs into lush green vegetation the moment rainwater touches it. That resurrection you can see with your own two eyes (afalā yubsirūn).'
    },
    {
      id: 10,
      ayahRef: 'Ayahs 28–30',
      title: 'The Dignity of Knowing When to Step Back',
      prompt: 'At the close of the Surah, cynical mockers demand: "When is this victory if you are truthful?" Instead of arguing or naming a date, Allah commands the Messenger: "So turn away from them, and wait; indeed they too are waiting" (Ayah 30). What profound lesson does this closing instruction offer for dealing with bad-faith cynicism?',
      options: [
        {
          id: 'A',
          text: 'The believer should engage in endless debate and try to force agreement at any cost.',
          isCorrect: false
        },
        {
          id: 'B',
          text: 'When truth has been clearly laid out and cynical sarcasm replaces sincere inquiry, spiritual dignity lies in stepping away from futile debate and placing ultimate outcomes in God\'s hands.',
          isCorrect: true
        },
        {
          id: 'C',
          text: 'The Prophet was instructed to abandon his mission entirely and go into permanent seclusion.',
          isCorrect: false
        },
        {
          id: 'D',
          text: 'It proves that the divine promise of truth prevailing was never meant to materialize.',
          isCorrect: false
        }
      ],
      deepReflection: 'There is a critical distinction between a sincere seeker with heartfelt questions and a sarcastic provocateur looking for an argument. When cynics sneer "When is this supposed judgment day anyway?", counter-arguing only fuels their arrogance. The Quran equips the caller to truth with emotional boundary-setting: do not become hostage to someone else\'s cynicism. Speak the truth with compassion, but when they choose mockery, withdraw with graceful detachment (Fa a\'rid \'anhum), protect your peace, and allow reality and time to provide the ultimate verdict.'
    }
  ]
};
